import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { NodeEntry, PathElement } from '@alfresco/js-api';

import { AppStore } from '../../store/states/app.state';
import { currentSiteDocumentRefId } from '../../store/selectors/app.custom-selectors';
import { WkSiteContainerModel } from '../../model/wk-site-children.model';
import { NodeInfoService } from '../../services/node.service';
import { CONSTANTS, SITE_CONTAINER_LABELS } from '../../model/custom-adf-model';

@Component({
  selector: 'wk-site-containers',
  templateUrl: './wk-site-containers.component.html',
  styleUrls: ['wk-site-containers.component.scss'],
  standalone: false,
})
export class WkSiteContainersComponent implements OnChanges, OnInit, OnDestroy {
  protected siteContainers: WkSiteContainerModel[];
  protected onDestroy$ = new Subject<void>();
  protected siteContainerLabels = SITE_CONTAINER_LABELS;
  /**
   * Observable of current site's containers(child folder with st:siteContainer aspect and otherwise)
   */
  siteContainers$: Observable<WkSiteContainerModel[]>;
  /**
   * When the node required to show the tree view loads before the current site's containers,
   * we store that node.
   */
  nodeLoadedFirst: NodeEntry;
  /**
   * current container selected to show the tree view
   */
  containerSelected: string;
  /**
   * Primarily used for checking the state of the panel between expanded and collapsed
   */
  @Input()
  data: any;
  @Input() fromContainers = true;

  constructor(
    public readonly store: Store<AppStore>,
    private readonly route: Router,
    private readonly nodeInfoService: NodeInfoService
  ) {}

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public ngOnInit(): void {
    this.siteContainers$ = this.store.select(currentSiteDocumentRefId).pipe(
      filter((siteChilren: WkSiteContainerModel[]) => !!siteChilren.length),
      map(
        // arrange the children to move document library at top.
        (siteChildren: WkSiteContainerModel[]) => {
          if (siteChildren[0].name === CONSTANTS.DOCUMENT_LIBRARY) {
            return [...siteChildren];
          }
          const sortedChildren: WkSiteContainerModel[] = [];
          siteChildren.forEach((child: WkSiteContainerModel) => {
            if (child.name === CONSTANTS.DOCUMENT_LIBRARY) {
              sortedChildren.unshift(child);
            } else {
              sortedChildren.push(child);
            }
          });
          return sortedChildren;
        }
      ),
      tap((siteChildren: WkSiteContainerModel[]) => {
        this.siteContainers = siteChildren;
        if (this.nodeLoadedFirst) {
          this.setContainerFolderForExpansion(this.nodeLoadedFirst);
          this.nodeLoadedFirst = null;
        }
      })
    );
    //If there is a route navigation which is not libraries or preview, reset the selected container's value
    //to hide the tree view.
    this.route.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event: NavigationEnd) => {
        if (
          !event.urlAfterRedirects.includes('libraries') &&
          !event.urlAfterRedirects.includes('preview')
        ) {
          this.nodeInfoService.setCurrentNode(null);
        }
      });
    this.nodeInfoService
      .getCurrentNode()
      .pipe(
        tap((node: NodeEntry) => {
          if (!node) {
            this.containerSelected = '';
          }
        }),
        filter((nodeEntry) => Boolean(nodeEntry)),
        takeUntil(this.onDestroy$)
      )
      .subscribe((node: NodeEntry) =>
        this.setContainerFolderForExpansion(node)
      );
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.data = changes.data.currentValue;
  }

  /**
   * Navigates to the container node.
   * @param nodeclicked Container node
   */
  public containerClicked(nodeclicked: WkSiteContainerModel) {
    this.route.navigate([`libraries/${nodeclicked.id}`]);
  }

  /**
   * Checks if the node loaded in the view is either a container node or its descendant
   * to expand the tree node
   * @param node the current node loaded in the view
   */
  private setContainerFolderForExpansion(node: NodeEntry): void {
    this.containerSelected = '';
    // there may be a case when the node shows up first even before we have the list of containers
    // of a given site. In that case just set it and wait for containers to load, which will call this function again.
    if (!this.siteContainers) {
      this.nodeLoadedFirst = node;
      return;
    }
    const rootElement = this.siteContainers.find(
      (sibling: WkSiteContainerModel) => {
        if (sibling.id === node.entry.id) {
          return true;
        }
        const rootPathElement = node.entry.path.elements.find(
          (path: PathElement) => path.id === sibling.id
        );
        return !!rootPathElement;
      }
    );
    if (rootElement) {
      this.containerSelected = rootElement.name;
    }
  }

  /**
   * Expand the container node in the view.
   * @param node container node
   */
  public expandContainer(node: WkSiteContainerModel): void {
    this.nodeInfoService.setCurrentNode({ entry: node.entry });
  }

  /**
   * Collapse the container node in the view
   */
  public collapseContainer() {
    this.nodeInfoService.setCurrentNode(null);
  }

  /**
   * This method will be called when list component emits clickedEvent
   * @param nodeEvent NodeEntry
   */
  public onClickTreeItem(nodeEvent: NodeEntry): void {
    this.route.navigate([`libraries/${nodeEvent.entry.id}`]);
  }

  /**
   * This method will be called if any error occurs while showing tree view of library
   */
  public onErrorOccurred(error) {
    console.warn(` --- handle error of browse tree --- ${error}`);
  }
}
